/**
 * <s-gallery> — product gallery arrows and thumbnails.
 * Scrolls the snap scroller ([data-product-gallery]) one slide at a time and keeps
 * the active thumbnail in sync with aria-current.
 */
class SGallery extends HTMLElement {
  #ac;

  connectedCallback() {
    const track = this.querySelector('[data-product-gallery]');
    if (!track) return;
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const slides = () => [...track.children];
    const thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    const smooth = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    const current = () => {
      const width = track.clientWidth || 1;
      return Math.round(Math.abs(track.scrollLeft) / width);
    };
    const go = (index) => {
      const list = slides();
      const target = list[Math.max(0, Math.min(list.length - 1, index))];
      if (target) track.scrollTo({ left: target.offsetLeft - track.offsetLeft, behavior: smooth });
    };

    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => go(current() - 1), { signal });
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => go(current() + 1), { signal });
    thumbs.forEach((thumb) => thumb.addEventListener('click', () => go(Number(thumb.dataset.galleryThumb)), { signal }));

    const counter = this.querySelector('[data-gallery-counter]');
    track.addEventListener(
      'scroll',
      () => {
        const index = current();
        thumbs.forEach((thumb, i) => (i === index ? thumb.setAttribute('aria-current', 'true') : thumb.removeAttribute('aria-current')));
        if (counter) counter.textContent = `${index + 1}/${counter.dataset.total}`;
      },
      { passive: true, signal }
    );

    // After a variant refresh the product-form partial carries the variant's featured media id; scroll to that slide.
    const form = document.querySelector('[data-product-form]');
    if (form) {
      const observer = new MutationObserver(() => {
        const mediaId = form.querySelector('[data-variant-media]')?.dataset.variantMedia;
        if (!mediaId) return;
        const index = slides().findIndex((slide) => slide.dataset.mediaId === mediaId);
        if (index >= 0 && index !== current()) go(index);
      });
      observer.observe(form, { childList: true, subtree: true });
      signal.addEventListener('abort', () => observer.disconnect());
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }
}

if (!customElements.get('s-gallery')) customElements.define('s-gallery', SGallery);
